import type { ValidatorMessage } from 'helpers/validators';
import type { FieldProps } from 'informed';
import { asField } from 'informed';
import type { InputHTMLAttributes } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomFormBlockFieldProps } from './contracts';
import type { InputBlockProps } from './primitives/InputBlock';
import { InputBlock } from './primitives/InputBlock';
import type { InputStructureProps } from './structures/InputStructure';
import { getErrorTranslation } from './utils';

export function asBlockField<Attr, P extends InputStructureProps & Attr, V>(
  Component: React.ComponentType<P>
): React.ComponentType<FieldProps<V, any> & P & CustomFormBlockFieldProps<V>> {
  return asField(
    ({
      fieldState,
      fieldApi,
      render,
      onChange,
      onFocus,
      onBlur,
      onKeyPress,
      translateError = true,
      transformValue,
      forwardedRef,
      field,
      touchOnChange = false,
      error: propsError,
      whiteWhenFilled,
      ...rest
    }) => {
      const { t } = useTranslation();

      const { value, error } = fieldState;
      const { setValue, setTouched } = fieldApi;

      const [isInputFocused, setIsInputFocused] = useState<boolean>(false);

      const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;

          (setValue as any)(transformValue ? transformValue(value) : value, e);
          touchOnChange && setTouched(true);

          if (onChange) {
            onChange(e);
          }
        },
        [onChange, setValue, setTouched, transformValue, touchOnChange]
      );

      const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
          setIsInputFocused(true);

          if (onFocus) {
            onFocus(e);
          }
        },
        [onFocus]
      );

      const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
          setTouched(true);
          setIsInputFocused(false);

          if (onBlur) {
            onBlur(e);
          }
        },
        [onBlur, setTouched]
      );

      const getError = useCallback(() => {
        if (propsError != null) {
          return propsError;
        }
        if (error != null && translateError) {
          return getErrorTranslation(t, error as ValidatorMessage);
        }
        return error;
      }, [t, translateError, propsError, error]);

      return (
        // FIXME: typescript error
        // @ts-ignore
        <Component
          {...rest}
          error={getError()}
          isInputFocused={isInputFocused}
          name={field}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          ref={forwardedRef}
          value={value == null ? '' : value}
          whiteWhenFilled={whiteWhenFilled}
        />
      );
    }
  );
}

export const Input = asBlockField<
  InputHTMLAttributes<HTMLInputElement>,
  InputBlockProps & React.RefAttributes<HTMLInputElement>,
  string
>(InputBlock);
